import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Drawer, DrawerContent, DrawerFooter,
} from "@/components/ui/drawer";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Camera, ChevronsUpDown, Check, Clock, Info, Loader2, X } from "lucide-react";
import { useYardAction, type YardDirection } from "@/hooks/useYardAction";
import { CheckInForm, type CheckInFormHandle } from "./CheckInForm";
import { TrailerIdCombobox } from "./TrailerIdCombobox";
import { YardPhotoCapture } from "./YardPhotoCapture";
import { BolUploadButton } from "@/components/driver/BolUploadButton";
import { useTrailerTypes } from "@/hooks/useTrailerTypes";
import { validateImageFiles } from "@/lib/validation";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";
import { DwellTimeChip } from "@/components/yard/DwellTimeChip";
import { NeedsServiceChip } from "@/components/yard/NeedsServiceChip";
import { TRAILER_ARRIVED_STATUS } from "@/lib/trailer-yard-ops";
import { useTenant } from "@/hooks/useTenant";
import { useObjectUrl } from "@/lib/object-url";
import {
  filterCheckInCandidates,
  describeCheckInImpact,
  describeLocationConflict,
} from "@/lib/yard-checkin-candidates";

type OrderStatus = Database["public"]["Enums"]["order_status"];

interface Props {
  locationId: string;
  locationCity: string;
  locationName: string;
}

function OrderPhotoButton({ orderId, photo, onPhotoChange }: { orderId: string; photo: File | null; onPhotoChange: (file: File | null) => void }) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const previewUrl = useObjectUrl(photo);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const { valid, errors } = validateImageFiles(files);
    if (errors.length > 0) errors.forEach((err) => toast.error(err));
    if (valid.length > 0) onPhotoChange(valid[0]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="mt-2" onClick={(e) => e.stopPropagation()}>
      <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileChange} className="hidden" />
      {photo && previewUrl ? (
        <div className="space-y-1.5">
          <img src={previewUrl} alt="Trailer photo" decoding="async" loading="lazy" className="w-full max-h-32 rounded-md object-contain border bg-muted" />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => fileInputRef.current?.click()}>
              <Camera className="h-3.5 w-3.5 mr-1.5" />Retake
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onPhotoChange(null)}>Remove</Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" className="w-full" onClick={() => fileInputRef.current?.click()}>
          <Camera className="h-3.5 w-3.5 mr-1.5" />Trailer Photo
        </Button>
      )}
    </div>
  );
}

function TruckIdCombobox({ value, onChange, tractors }: { value: string; onChange: (val: string) => void; tractors: { id: string; name: string }[] }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = search
    ? tractors.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()))
    : tractors;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between">
          {value || <span className="text-muted-foreground">Select or enter truck ID...</span>}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type or search truck..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {search && !tractors.some((t) => t.name.toLowerCase() === search.toLowerCase()) && (
              <CommandItem
                value={search}
                onSelect={() => { onChange(search); setSearch(""); setOpen(false); }}
              >
                Use "{search}"
              </CommandItem>
            )}
            {filtered.length > 0 && (
              <CommandGroup heading="Tractors">
                {filtered.map((t) => (
                  <CommandItem
                    key={t.id}
                    value={t.name}
                    onSelect={() => { onChange(t.name); setSearch(""); setOpen(false); }}
                  >
                    <Check className={`mr-2 h-4 w-4 ${value === t.name ? "opacity-100" : "opacity-0"}`} />
                    {t.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {filtered.length === 0 && !search && (
              <CommandEmpty>No tractors available.</CommandEmpty>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function YardSiteView({ locationId, locationCity, locationName }: Props) {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const [showManualPage, setShowManualPage] = useState(false);
  const [manualMode, setManualMode] = useState<"checkin" | "checkout">("checkin");
  const checkInSubmitRef = useRef<CheckInFormHandle | null>(null);
  const [checkInReady, setCheckInReady] = useState(false);
  const [showConfirmDrawer, setShowConfirmDrawer] = useState(false);

  // -- Order-based overlay state --
  const [orderCheckoutOverlay, setOrderCheckoutOverlay] = useState<any>(null);
  const [orderCheckinOverlay, setOrderCheckinOverlay] = useState<any>(null);
  const [showOrderConfirmDrawer, setShowOrderConfirmDrawer] = useState(false);
  const [orderOverlayTruckId, setOrderOverlayTruckId] = useState("");

  // -- Checkout state --
  const [selectedCheckoutOrder, setSelectedCheckoutOrder] = useState<any>(null);
  const [showCheckoutConfirm, setShowCheckoutConfirm] = useState(false);
  const [checkoutPhotos, setCheckoutPhotos] = useState<Record<string, File | null>>({});
  const [manualTrailerId, setManualTrailerId] = useState("");
  // Set when "Check in" is pressed on a Pending check-in card: the manual
  // check-in form opens locked to that trailer.
  const [pendingCheckinTrailerId, setPendingCheckinTrailerId] = useState<string | null>(null);
  const [showManualCheckoutConfirm, setShowManualCheckoutConfirm] = useState(false);
  const [showNewTrailerConfirm, setShowNewTrailerConfirm] = useState(false);
  const [newTrailerTypeId, setNewTrailerTypeId] = useState<string>("");
  const [manualPhoto, setManualPhoto] = useState<File | null>(null);
  const [checkoutTruckId, setCheckoutTruckId] = useState("");
  const [manualSealStatus, setManualSealStatus] = useState<"intact" | "broken">("intact");
  const [orderOverlaySealStatus, setOrderOverlaySealStatus] = useState<"intact" | "broken">("intact");

  // -- Checkin state --
  const [selectedCheckinOrder, setSelectedCheckinOrder] = useState<any>(null);
  const [showCheckinConfirm, setShowCheckinConfirm] = useState(false);
  const [checkinPhotos, setCheckinPhotos] = useState<Record<string, File | null>>({});
  const [checkInPhoto, setCheckInPhoto] = useState<File | null>(null);
  const [checkInPhotoRight, setCheckInPhotoRight] = useState<File | null>(null);
  const [checkInSealPhoto, setCheckInSealPhoto] = useState<File | null>(null);
  const [checkInLicensePlatePhoto, setCheckInLicensePlatePhoto] = useState<File | null>(null);
  const [checkInBolPhoto, setCheckInBolPhoto] = useState<File | null>(null);

  // -- Order checkin overlay state --
  const orderCheckInSubmitRef = useRef<CheckInFormHandle | null>(null);
  const [orderCheckInReady, setOrderCheckInReady] = useState(false);
  const [orderCheckInPhoto, setOrderCheckInPhoto] = useState<File | null>(null);
  const [orderCheckInPhotoRight, setOrderCheckInPhotoRight] = useState<File | null>(null);
  const [orderCheckInSealPhoto, setOrderCheckInSealPhoto] = useState<File | null>(null);
  const [orderCheckInLicensePlatePhoto, setOrderCheckInLicensePlatePhoto] = useState<File | null>(null);
  const [orderCheckInBolPhoto, setOrderCheckInBolPhoto] = useState<File | null>(null);

  const { data: trailerTypes } = useTrailerTypes();

  React.useEffect(() => {
    if (trailerTypes?.length && !newTrailerTypeId) {
      setNewTrailerTypeId(trailerTypes[0].id);
    }
  }, [trailerTypes, newTrailerTypeId]);

  // -- Queries --

  // Trailers physically on site
  const { data: onSiteTrailerCount = 0 } = useQuery({
    queryKey: ["yard-site-trailer-count", locationId, tenantId],
    queryFn: async () => {
      let query = supabase
        .from("trailers")
        .select("id", { count: "exact", head: true })
        .eq("current_location_id", locationId)
        .eq("current_location_status", "at_site");
      if (tenantId) query = query.eq("tenant_id", tenantId);
      const { count, error } = await query;
      if (error) throw error;
      return count || 0;
    },
    enabled: !!locationId,
  });

  // On-site trailers for combobox
  const { data: onSiteTrailers = [] } = useQuery({
    queryKey: ["trailers-on-site", locationId, tenantId],
    queryFn: async () => {
      let query = supabase
        .from("trailers")
        .select("id, trailer_id, scac, load_status, seal_id, seal_status, trailer_type_id, is_customer_parking, needs_service, needs_service_reason")
        .eq("current_location_id", locationId)
        .eq("current_location_status", "at_site")
        .order("trailer_id");
      if (tenantId) query = query.eq("tenant_id", tenantId);
      const { data } = await query;
      return data || [];
    },
    enabled: !!locationId,
  });

  // Trailers EAPI reported at this yard that nobody has checked in yet.
  const { data: pendingCheckinTrailers = [] } = useQuery({
    queryKey: ["trailers-pending-checkin", locationId, tenantId],
    queryFn: async () => {
      let query = supabase
        .from("trailers")
        .select("id, trailer_id, scac, trailer_type_id")
        .eq("current_location_id", locationId)
        .eq("current_location_status", TRAILER_ARRIVED_STATUS)
        .order("trailer_id");
      if (tenantId) query = query.eq("tenant_id", tenantId);
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!locationId,
  });

  // Fetch last check-in event times for on-site trailers (for dwell time)
  const onSiteIds = onSiteTrailers.map((t) => t.id);
  const { data: dwellEvents } = useQuery({
    queryKey: ["yard-dwell-events", onSiteIds, tenantId],
    enabled: onSiteIds.length > 0,
    queryFn: async () => {
      let query = supabase
        .from("trailer_events")
        .select("trailer_id, occurred_at")
        .in("trailer_id", onSiteIds)
        .eq("event_type", "checked_in")
        .order("occurred_at", { ascending: false });
      if (tenantId) query = query.eq("tenant_id", tenantId);
      const { data, error } = await query;
      if (error) throw error;
      const map = new Map<string, string>();
      for (const e of data) {
        if (!map.has(e.trailer_id)) map.set(e.trailer_id, e.occurred_at);
      }
      return map;
    },
  });

  // Get the selected trailer's details and latest check-in event
  const selectedCheckoutTrailer = onSiteTrailers.find(
    (t) => t.trailer_id === manualTrailerId
  );

  const { data: checkInEvent } = useQuery({
    queryKey: ["trailer-checkin-event", selectedCheckoutTrailer?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("trailer_events")
        .select("truck_id, seal_id, load_status")
        .eq("trailer_id", selectedCheckoutTrailer!.id)
        .eq("event_type", "checked_in")
        .order("occurred_at", { ascending: false })
        .limit(1)
        .single();
      return data;
    },
    enabled: !!selectedCheckoutTrailer?.id,
  });

  // Set truck ID from check-in event when trailer is selected
  React.useEffect(() => {
    if (checkInEvent?.truck_id) {
      setCheckoutTruckId(checkInEvent.truck_id);
    } else {
      setCheckoutTruckId("");
    }
  }, [checkInEvent, manualTrailerId]);

  // Initialize manual seal status from existing trailer record
  React.useEffect(() => {
    const existing = (selectedCheckoutTrailer as any)?.seal_status;
    setManualSealStatus(existing === "broken" ? "broken" : "intact");
  }, [selectedCheckoutTrailer]);

  // Query for order-checkout overlay trailer details
  const orderCheckoutTrailer = onSiteTrailers.find(
    (t) => t.trailer_id === orderCheckoutOverlay?.trailer_id
  );

  const { data: orderCheckoutEvent } = useQuery({
    queryKey: ["trailer-checkin-event-order", orderCheckoutTrailer?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("trailer_events")
        .select("truck_id, seal_id, load_status")
        .eq("trailer_id", orderCheckoutTrailer!.id)
        .eq("event_type", "checked_in")
        .order("occurred_at", { ascending: false })
        .limit(1)
        .single();
      return data;
    },
    enabled: !!orderCheckoutTrailer?.id,
  });

  // Set truck ID when order checkout overlay opens
  React.useEffect(() => {
    if (orderCheckoutOverlay && orderCheckoutEvent?.truck_id) {
      setOrderOverlayTruckId(orderCheckoutEvent.truck_id);
    } else if (!orderCheckoutOverlay) {
      setOrderOverlayTruckId("");
    }
  }, [orderCheckoutEvent, orderCheckoutOverlay]);

  // Initialize order overlay seal status from existing trailer record
  React.useEffect(() => {
    const existing = (orderCheckoutTrailer as any)?.seal_status;
    setOrderOverlaySealStatus(existing === "broken" ? "broken" : "intact");
  }, [orderCheckoutTrailer, orderCheckoutOverlay]);


  // Tractors for truck ID combobox
  const { data: tractorsList = [] } = useQuery({
    queryKey: ["tractors-lookup"],
    queryFn: async () => {
      const { data } = await supabase
        .from("tractors")
        .select("id, name")
        .order("name");
      return data || [];
    },
  });

  const { data: checkoutOrders = [], isLoading: loadingCheckout } = useQuery({
    queryKey: ["yard-checkout-orders", locationCity, tenantId],
    queryFn: async () => {
      let query = supabase
        .from("orders")
        .select("id, unique_id, trailer_id, assigned_tractor_id, status, ready_time, tractors(name)")
        .eq("ship_from_city", locationCity)
        .eq("is_work_order", false)
        .eq("status", "confirmed" as OrderStatus)
        .not("trailer_id", "is", null)
        .neq("trailer_id", "");
      if (tenantId) query = query.eq("tenant_id", tenantId);
      const { data, error } = await query;
      if (error) throw error;
      const now = new Date();
      const pastCutoff = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const futureCutoff = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      return (data || []).filter((o: any) => {
        const t = new Date(o.ready_time);
        return t >= pastCutoff && t <= futureCutoff;
      });
    },
    enabled: !!locationCity,
  });

  // Checkin orders (arriving at this site).
  // Leg-aware: query the `legs` table for legs whose ship_to_city matches this
  // yard and whose driver_status hasn't been delivered yet. Each candidate
  // surfaces with its parent order metadata so the UI/check-in action can
  // promote only the matching leg (not the entire order).
  const { data: checkinOrders = [], isLoading: loadingCheckin } = useQuery({
    queryKey: ["yard-checkin-orders", locationId, tenantId],
    queryFn: async () => {
      let query = supabase
        .from("legs")
        .select(
          `id, leg_sequence, ship_to_city, ship_to_location_id, driver_status, ready_time, requested_delivery_time, order_id, is_loaded,
           orders!inner(id, unique_id, trailer_id, assigned_tractor_id, status, is_work_order, tenant_id)`
        )
        // Restrict to legs whose destination is THIS AYMS-tracked yard.
        // Matching by location_id (not city) ensures we ignore legs going to
        // other locations that happen to share the same city name and are
        // not tracked in AYMS.
        .eq("ship_to_location_id", locationId)
        .or("driver_status.is.null,driver_status.neq.delivered")
        .eq("orders.is_work_order", false)
        .in("orders.status", ["confirmed", "in_transit"] as OrderStatus[])
        .not("orders.trailer_id", "is", null)
        .neq("orders.trailer_id", "");
      if (tenantId) query = query.eq("tenant_id", tenantId);
      const { data, error } = await query;
      if (error) throw error;

      // Sibling counts + statuses per parent, for the "Leg N of M" chip and
      // the "this closes the whole order" warning.
      const parentIds = Array.from(new Set((data || []).map((r: any) => r.order_id)));
      const siblingCounts = new Map<string, number>();
      const siblingStatuses = new Map<string, (string | null)[]>();
      if (parentIds.length) {
        const { data: siblings } = await supabase
          .from("legs")
          .select("order_id, driver_status")
          .in("order_id", parentIds);
        (siblings || []).forEach((s: any) => {
          siblingCounts.set(s.order_id, (siblingCounts.get(s.order_id) || 0) + 1);
          siblingStatuses.set(s.order_id, [
            ...(siblingStatuses.get(s.order_id) || []),
            s.driver_status ?? null,
          ]);
        });
      }

      // Hydrate tractor names (no FK on legs view path)
      const tractorIds = Array.from(
        new Set((data || []).map((r: any) => r.orders?.assigned_tractor_id).filter(Boolean))
      ) as string[];
      const tractorsById = new Map<string, any>();
      if (tractorIds.length) {
        const { data: tractors } = await supabase
          .from("tractors")
          .select("id, name")
          .in("id", tractorIds);
        (tractors || []).forEach((t: any) => tractorsById.set(t.id, t));
      }

      const mapped = (data || []).map((r: any) => ({
        id: r.orders.id,
        unique_id: r.orders.unique_id,
        trailer_id: r.orders.trailer_id,
        assigned_tractor_id: r.orders.assigned_tractor_id,
        status: r.orders.status,
        requested_delivery_time: r.requested_delivery_time,
        tractors: r.orders.assigned_tractor_id
          ? tractorsById.get(r.orders.assigned_tractor_id) ?? null
          : null,
        legId: r.id,
        is_loaded: r.is_loaded,
        legSequence: r.leg_sequence,
        legCount: siblingCounts.get(r.order_id) ?? 1,
        driverStatus: r.driver_status ?? null,
        readyTime: r.ready_time ?? null,
        siblingStatuses: siblingStatuses.get(r.order_id) ?? [],
      }));

      // Drop legs that are outside the arrival window, or that have not
      // started yet on a multi-leg order (AOP-VLTK-00144).
      return filterCheckInCandidates(mapped);

    },
    enabled: !!locationId,
  });

  // Last yard event for the trailer being checked in — used to warn when the
  // trailer was most recently checked in at a DIFFERENT site (AOP-VLTK-00144,
  // where the same trailer was checked in at two yards three minutes apart).
  const { data: checkinLastEvent } = useQuery({
    queryKey: ["yard-checkin-last-event", orderCheckinOverlay?.trailer_id, tenantId],
    enabled: !!orderCheckinOverlay?.trailer_id,
    queryFn: async () => {
      let trailerQuery = supabase
        .from("trailers")
        .select("id")
        .eq("trailer_id", orderCheckinOverlay.trailer_id)
        .limit(1);
      if (tenantId) trailerQuery = trailerQuery.eq("tenant_id", tenantId);
      const { data: trailerRows } = await trailerQuery;
      const trailerDbId = trailerRows?.[0]?.id;
      if (!trailerDbId) return null;
      const { data: events } = await supabase
        .from("trailer_events")
        .select("event_type, location_id, occurred_at, location:locations!trailer_events_location_id_fkey(city)")
        .eq("trailer_id", trailerDbId)
        .order("occurred_at", { ascending: false })
        .limit(1);
      const ev: any = events?.[0];
      if (!ev) return null;
      return { event_type: ev.event_type, location_id: ev.location_id, city: ev.location?.city ?? null };

    },
  });
  const checkinLocationConflict = describeLocationConflict(checkinLastEvent, locationId);

  // -- Yard actions --

  const currentCheckoutPhoto = selectedCheckoutOrder ? (checkoutPhotos[selectedCheckoutOrder.id] ?? null) : null;
  const currentCheckinPhoto = selectedCheckinOrder ? (checkinPhotos[selectedCheckinOrder.id] ?? null) : null;

  const checkoutAction = useYardAction({
    locationId,
    locationCity,
    direction: "out",
    pendingPhoto: currentCheckoutPhoto ?? manualPhoto,
    onPhotoUploaded: () => {
      if (selectedCheckoutOrder) setCheckoutPhotos((p) => ({ ...p, [selectedCheckoutOrder.id]: null }));
      setManualPhoto(null);
    },
  });

  const checkinAction = useYardAction({
    locationId,
    locationCity,
    direction: "in",
    pendingPhoto: currentCheckinPhoto ?? orderCheckInPhoto ?? checkInPhoto,
    pendingPhotoRight: orderCheckInPhotoRight ?? checkInPhotoRight,
    pendingSealPhoto: orderCheckInSealPhoto ?? checkInSealPhoto,
    pendingLicensePlatePhoto: orderCheckInLicensePlatePhoto ?? checkInLicensePlatePhoto,
    pendingBolPhoto: checkInBolPhoto,
    onPhotoUploaded: () => {
      if (selectedCheckinOrder) setCheckinPhotos((p) => ({ ...p, [selectedCheckinOrder.id]: null }));
      setCheckInPhoto(null);
      setOrderCheckInPhoto(null);
    },
    onPhotoRightUploaded: () => {
      setCheckInPhotoRight(null);
      setOrderCheckInPhotoRight(null);
    },
    onSealPhotoUploaded: () => {
      setCheckInSealPhoto(null);
      setOrderCheckInSealPhoto(null);
    },
    onLicensePlatePhotoUploaded: () => {
      setCheckInLicensePlatePhoto(null);
      setOrderCheckInLicensePlatePhoto(null);
    },
    onBolPhotoUploaded: () => setCheckInBolPhoto(null),
  });

  /**
   * Only one yard action can run at a time, so a single label covers every
   * busy button — it reports the actual step (upload N of M, refreshing…)
   * instead of a static "Processing…" that looks like a frozen screen.
   */
  const busyLabel =
    checkoutAction.progressLabel ?? checkinAction.progressLabel ?? "Processing…";
  const anyProcessing = checkoutAction.isProcessing || checkinAction.isProcessing;

  // -- Handlers --
  const handleCheckoutOrder = async () => {
    if (!selectedCheckoutOrder) return;
    await checkoutAction.processOrderAction(selectedCheckoutOrder);
    setShowCheckoutConfirm(false);
    setSelectedCheckoutOrder(null);
  };

  const handleCheckinOrder = async () => {
    if (!selectedCheckinOrder) return;
    await checkinAction.processOrderAction(selectedCheckinOrder);
    setShowCheckinConfirm(false);
    setSelectedCheckinOrder(null);
  };

  const handleManualCheckout = async () => {
    const trimmed = manualTrailerId.trim();
    if (!trimmed) return;
    const sealApplies = !!(selectedCheckoutTrailer && (selectedCheckoutTrailer.load_status === "loaded" || selectedCheckoutTrailer.seal_id));
    const found = await checkoutAction.processManualAction(trimmed, sealApplies ? { sealStatus: manualSealStatus } as any : undefined);
    if (found) {
      setManualTrailerId("");
      setShowConfirmDrawer(false);
      setShowManualPage(false); setManualMode("checkin");
    } else {
      setShowConfirmDrawer(false);
      if (trailerTypes?.length) setNewTrailerTypeId(trailerTypes[0].id);
      setShowNewTrailerConfirm(true);
    }
    setShowManualCheckoutConfirm(false);
  };

  const handleManualCheckinConfirm = () => {
    if (!checkInSubmitRef.current) return;
    checkInSubmitRef.current.submit();
    setShowConfirmDrawer(false);
  };

  const handleCheckinSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["yard-site-trailer-count", locationId, tenantId] });
    queryClient.invalidateQueries({ queryKey: ["trailers-on-site", locationId, tenantId] });
    queryClient.invalidateQueries({ queryKey: ["trailers-pending-checkin", locationId, tenantId] });
    setShowManualPage(false);
    setManualMode("checkin");
    setPendingCheckinTrailerId(null);
  };

  const handleCreateAndCheckout = async () => {
    await checkoutAction.createAndProcess(manualTrailerId.trim(), newTrailerTypeId);
    setManualTrailerId("");
    setShowNewTrailerConfirm(false);
    setShowManualPage(false); setManualMode("checkin");
  };

  // -- Order overlay handlers --
  const handleOrderCheckoutConfirm = async () => {
    if (!orderCheckoutOverlay) return;
    const sealApplies = !!(orderCheckoutTrailer && (orderCheckoutTrailer.load_status === "loaded" || orderCheckoutTrailer.seal_id));
    await checkoutAction.processOrderAction(orderCheckoutOverlay, sealApplies ? { sealStatus: orderOverlaySealStatus } : undefined);
    setShowOrderConfirmDrawer(false);
    setOrderCheckoutOverlay(null);
  };

  const handleOrderCheckinConfirm = async () => {
    if (!orderCheckinOverlay) return;
    // Same data manual check-in submits (incl. the inspection tick); the
    // order path only adds completing this order's leg.
    await checkinAction.processOrderAction(orderCheckinOverlay, orderCheckInSubmitRef.current?.checkInMeta());
    setShowOrderConfirmDrawer(false);
    setOrderCheckinOverlay(null);
  };

  const formatTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString([], {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-auto p-4 space-y-6 pb-24">
        {/* Stat cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border bg-card p-4">
            <p className="text-2xl font-bold tabular-nums">{onSiteTrailerCount}</p>
            <p className="text-sm text-muted-foreground">trailers on site</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-2xl font-bold tabular-nums">{pendingCheckinTrailers.length}</p>
            <p className="text-sm text-muted-foreground">needs check-in</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-2xl font-bold tabular-nums">{checkinOrders.length}</p>
            <p className="text-sm text-muted-foreground">trailers arriving</p>
          </div>
        </div>

        {/* Pending check-in — EAPI reported these trailers here; check them in first. */}
        {pendingCheckinTrailers.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3">Pending check-in</h3>
            <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-200">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <p>The customer reported these trailers have arrived. Perform check-in and inspection as soon as possible.</p>
            </div>
            <div className="space-y-2">
              {pendingCheckinTrailers.map((trailer) => {
                const trailerType = trailerTypes?.find((tt) => tt.id === trailer.trailer_type_id);
                return (
                  <Card key={trailer.id} className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-bold font-mono text-base">{trailer.trailer_id}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {[trailer.scac, trailerType?.label].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setPendingCheckinTrailerId(trailer.trailer_id);
                          setShowManualPage(true);
                          setManualMode("checkin");
                        }}
                      >
                        Check in
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* On site section — shows physical trailers, not just order-linked ones */}
        <div>
          <h3 className="text-lg font-semibold mb-3">On site</h3>
          {onSiteTrailers.length === 0 ? (
            <div className="rounded-lg bg-muted/50 p-8 text-center text-sm text-muted-foreground">
              No trailers on site
            </div>
          ) : (
            <div className="space-y-2">
              {onSiteTrailers.map((trailer: any) => {
                const matchingOrder = checkoutOrders.find((o: any) => o.trailer_id === trailer.trailer_id);
                const trailerType = trailerTypes?.find((tt) => tt.id === trailer.trailer_type_id);
                return (
                  <Card key={trailer.id} className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-bold font-mono text-base">{trailer.trailer_id}</p>
                          {dwellEvents?.get(trailer.id) && (
                            <DwellTimeChip occurredAt={dwellEvents.get(trailer.id)!} />
                          )}
                          {trailer.is_customer_parking && (
                            <Badge variant="outline" className="text-xs w-fit">Customer Parking</Badge>
                          )}
                          <NeedsServiceChip
                            needsService={trailer.needs_service}
                            reason={trailer.needs_service_reason}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {[trailer.scac, trailerType?.label, trailer.load_status && trailer.load_status !== "unknown" ? trailer.load_status : null].filter(Boolean).join(" · ")}
                        </p>
                        {matchingOrder && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {matchingOrder.unique_id} · Departing {formatTime(matchingOrder.ready_time)}
                          </p>
                        )}
                      </div>
                      {matchingOrder ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setOrderCheckoutOverlay(matchingOrder)}
                        >
                          Check out
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setManualTrailerId(trailer.trailer_id);
                            setShowManualPage(true);
                            setManualMode("checkout");
                          }}
                        >
                          Check out
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Upcoming arrivals section */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Upcoming arrivals</h3>
          {loadingCheckin ? (
            <div className="rounded-lg bg-muted/50 p-8 text-center text-sm text-muted-foreground animate-pulse">Loading...</div>
          ) : checkinOrders.length === 0 ? (
            <div className="rounded-lg bg-muted/50 p-8 text-center text-sm text-muted-foreground">
              No upcoming arrivals
            </div>
          ) : (
            <div className="space-y-2">
              {checkinOrders.map((order: any) => (
                <Card key={order.id} className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-bold font-mono text-base">{order.trailer_id}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {order.unique_id} • {order.tractors?.name || "No truck"}
                      </p>
                      {order.legCount > 1 && (
                        <p className="text-xs text-muted-foreground">
                          Leg {order.legSequence} of {order.legCount}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Arriving {formatTime(order.requested_delivery_time)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setOrderCheckinOverlay(order)}
                    >
                      Check in
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fixed bottom button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t">
        <Button
          className="w-full"
          size="lg"
          onClick={() => { setShowManualPage(true); setManualMode("checkin"); }}
        >
          Manual gate check
        </Button>
      </div>

      {/* Manual gate check full-page overlay */}
      {showManualPage && (
        <div className="fixed inset-0 z-50 bg-background overflow-auto">
          <div className="p-8 pb-24">
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => { setShowManualPage(false); setManualMode("checkin"); setPendingCheckinTrailerId(null); }}
                className="p-1.5 rounded-md hover:bg-muted transition-colors active:scale-95"
              >
                <X className="h-6 w-6" />
              </button>
              <h2 className="text-2xl font-semibold">Manual gate check</h2>
            </div>

            {/* Toggle between check in and check out */}
            <div className="flex rounded-lg border bg-white dark:bg-muted p-1 mb-6">
              <button
                onClick={() => setManualMode("checkin")}
                className={`flex-1 flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-medium transition-colors ${
                  manualMode === "checkin"
                    ? "bg-primary text-primary-foreground shadow-sm dark:bg-background dark:text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <ArrowDownRight className="h-4 w-4" />
                Check in
              </button>
              <button
                onClick={() => setManualMode("checkout")}
                className={`flex-1 flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-medium transition-colors ${
                  manualMode === "checkout"
                    ? "bg-primary text-primary-foreground shadow-sm dark:bg-background dark:text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <ArrowUpRight className="h-4 w-4" />
                Check out
              </button>
            </div>

            {manualMode === "checkin" && (
              <div className="flex-1 overflow-auto">
                <CheckInForm
                  key={pendingCheckinTrailerId ?? "manual"}
                  lockedTrailerId={pendingCheckinTrailerId ?? undefined}
                  locationId={locationId}
                  locationCity={locationCity}
                  locationName={locationName}
                  isProcessing={checkinAction.isProcessing}
                  pendingPhoto={checkInPhoto}
                  onPhotoChange={setCheckInPhoto}
                  pendingPhotoRight={checkInPhotoRight}
                  onPhotoRightChange={setCheckInPhotoRight}
                  pendingSealPhoto={checkInSealPhoto}
                  onSealPhotoChange={setCheckInSealPhoto}
                  pendingLicensePlatePhoto={checkInLicensePlatePhoto}
                  onLicensePlatePhotoChange={setCheckInLicensePlatePhoto}
                  pendingBolPhoto={checkInBolPhoto}
                  onBolPhotoChange={setCheckInBolPhoto}
                  processManualAction={checkinAction.processManualAction}
                  createAndProcess={checkinAction.createAndProcess}
                  submitRef={checkInSubmitRef}
                  onReadyChange={setCheckInReady}
                  onSuccess={handleCheckinSuccess}
                />
              </div>
            )}

            {manualMode === "checkout" && (
              <div>
                <div className="space-y-4">
                  <label className="text-sm font-medium block">Manual Trailer Check Out</label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label className="text-xs mb-1.5 block">Trailer ID *</Label>
                      <TrailerIdCombobox
                        value={manualTrailerId}
                        onChange={(v) => setManualTrailerId(v.toUpperCase())}
                        trailers={onSiteTrailers}
                        emptyMessage="No trailers at this site."
                      />
                    </div>
                  </div>

                  {/* Trailer details from check-in */}
                  {selectedCheckoutTrailer && manualTrailerId && (
                    <div className="mt-4 space-y-4">
                      {/* Truck ID - editable, above read-only list */}
                      <div>
                        <Label className="text-xs mb-1.5 block">Truck ID *</Label>
                        <TruckIdCombobox
                          value={checkoutTruckId}
                          onChange={setCheckoutTruckId}
                          tractors={tractorsList}
                        />
                      </div>

                      <div className="divide-y divide-border">
                        {selectedCheckoutTrailer.scac && (
                          <div className="flex justify-between items-start py-3">
                            <span className="text-sm text-muted-foreground">SCAC</span>
                            <span className="text-sm font-medium font-mono">{selectedCheckoutTrailer.scac}</span>
                          </div>
                        )}

                        {selectedCheckoutTrailer.trailer_type_id && (
                          <div className="flex justify-between items-start py-3">
                            <span className="text-sm text-muted-foreground">Trailer Type</span>
                            <span className="text-sm font-medium">{trailerTypes?.find((tt) => tt.id === selectedCheckoutTrailer.trailer_type_id)?.label || "—"}</span>
                          </div>
                        )}

                        {selectedCheckoutTrailer.load_status && selectedCheckoutTrailer.load_status !== "unknown" && (
                          <div className="flex justify-between items-start py-3">
                            <span className="text-sm text-muted-foreground">Load Status</span>
                            <span className="text-sm font-medium capitalize">{selectedCheckoutTrailer.load_status}</span>
                          </div>
                        )}

                        {selectedCheckoutTrailer.seal_id && (
                          <div className="flex justify-between items-start py-3">
                            <span className="text-sm text-muted-foreground">Seal ID</span>
                            <span className="text-sm font-medium font-mono">{selectedCheckoutTrailer.seal_id}</span>
                          </div>
                        )}
                      </div>

                      {(selectedCheckoutTrailer.load_status === "loaded" || selectedCheckoutTrailer.seal_id) && (
                        <div className="space-y-1.5">
                          <Label className="text-xs">Seal Status *</Label>
                          {(selectedCheckoutTrailer as any).seal_status && (
                            <p className="text-xs text-muted-foreground">
                              Last recorded: <span className="capitalize font-medium">{(selectedCheckoutTrailer as any).seal_status}</span>
                            </p>
                          )}
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              type="button"
                              variant={manualSealStatus === "intact" ? "default" : "outline"}
                              size="sm"
                              onClick={() => setManualSealStatus("intact")}
                            >
                              Intact
                            </Button>
                            <Button
                              type="button"
                              variant={manualSealStatus === "broken" ? "default" : "outline"}
                              size="sm"
                              onClick={() => setManualSealStatus("broken")}
                            >
                              Broken
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <YardPhotoCapture
                    photo={manualPhoto}
                    onPhotoChange={setManualPhoto}
                    label="Back of Trailer *"
                    helperText="The photo should clearly capture the trailer's license plate number"
                    captureLabel={manualPhoto ? "Retake Back of Trailer Photo" : "Back of Trailer Photo"}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Fixed bottom action bar */}
          <div className="fixed bottom-0 left-0 right-0 p-4 border-t z-50 bg-background">
            {manualMode === "checkin" ? (
              <Button
                className="w-full"
                size="lg"
                onClick={() => setShowConfirmDrawer(true)}
                disabled={!checkInReady || checkinAction.isProcessing}
              >
                {checkinAction.isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {busyLabel}
                  </>
                ) : (
                  "Check In"
                )}
              </Button>
            ) : (
              <Button
                className="w-full"
                size="lg"
                onClick={() => setShowConfirmDrawer(true)}
                disabled={
                  !manualTrailerId.trim() ||
                  !checkoutTruckId.trim() ||
                  !manualPhoto ||
                  checkoutAction.isProcessing
                }
              >
                {checkoutAction.isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {busyLabel}
                  </>
                ) : (
                  "Check Out"
                )}
              </Button>
            )}
          </div>

          {/* Confirmation half-sheet drawer */}
          <Drawer open={showConfirmDrawer} onOpenChange={setShowConfirmDrawer}>
            <DrawerContent>
              <div className="p-6 pt-4">
                <h2 className="text-2xl font-bold mb-1">
                  {manualMode === "checkin" ? "Check in" : "Check out"}{" "}
                  {manualMode === "checkin"
                    ? checkInSubmitRef.current?.trailerId || ""
                    : manualTrailerId}?
                </h2>
              </div>
              <DrawerFooter className="flex-row gap-3 pt-0">
                <Button
                  variant="outline"
                  className="flex-1"
                  size="lg"
                  onClick={() => setShowConfirmDrawer(false)}
                >
                  Go back
                </Button>
                <Button
                  className="flex-1"
                  size="lg"
                  onClick={() => {
                    if (manualMode === "checkin") {
                      handleManualCheckinConfirm();
                    } else {
                      handleManualCheckout();
                    }
                  }}
                  disabled={manualMode === "checkin" ? checkinAction.isProcessing : checkoutAction.isProcessing}
                >
                  {(manualMode === "checkin" ? checkinAction.isProcessing : checkoutAction.isProcessing) ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {busyLabel}
                    </>
                  ) : (
                    "Confirm"
                  )}
                </Button>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </div>
      )}

      {/* Order checkout full-page overlay */}
      {orderCheckoutOverlay && (
        <div className="fixed inset-0 z-50 bg-background overflow-auto">
          <div className="p-8 pb-24">
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => setOrderCheckoutOverlay(null)}
                className="p-1.5 rounded-md hover:bg-muted transition-colors active:scale-95"
              >
                <X className="h-6 w-6" />
              </button>
              <h2 className="text-2xl font-semibold">Check out Trailer {orderCheckoutOverlay.trailer_id}</h2>
            </div>

            <div className="space-y-4">
              {/* Truck ID - editable, above read-only list */}
              <div>
                <Label className="text-xs mb-1.5 block">Truck ID</Label>
                <TruckIdCombobox
                  value={orderOverlayTruckId}
                  onChange={setOrderOverlayTruckId}
                  tractors={tractorsList}
                />
              </div>

              <div className="divide-y divide-border">
                <div className="flex justify-between items-start py-3">
                  <span className="text-sm text-muted-foreground">Order</span>
                  <span className="text-sm font-medium font-mono">{orderCheckoutOverlay.unique_id}</span>
                </div>

                {orderCheckoutTrailer?.scac && (
                  <div className="flex justify-between items-start py-3">
                    <span className="text-sm text-muted-foreground">SCAC</span>
                    <span className="text-sm font-medium font-mono">{orderCheckoutTrailer.scac}</span>
                  </div>
                )}

                {orderCheckoutTrailer?.trailer_type_id && (
                  <div className="flex justify-between items-start py-3">
                    <span className="text-sm text-muted-foreground">Trailer Type</span>
                    <span className="text-sm font-medium">{trailerTypes?.find((tt) => tt.id === orderCheckoutTrailer.trailer_type_id)?.label || "—"}</span>
                  </div>
                )}

                {orderCheckoutTrailer?.load_status && orderCheckoutTrailer.load_status !== "unknown" && (
                  <div className="flex justify-between items-start py-3">
                    <span className="text-sm text-muted-foreground">Load Status</span>
                    <span className="text-sm font-medium capitalize">{orderCheckoutTrailer.load_status}</span>
                  </div>
                )}

                {orderCheckoutTrailer?.seal_id && (
                  <div className="flex justify-between items-start py-3">
                    <span className="text-sm text-muted-foreground">Seal ID</span>
                    <span className="text-sm font-medium font-mono">{orderCheckoutTrailer.seal_id}</span>
                  </div>
                )}
              </div>

              {orderCheckoutTrailer && (orderCheckoutTrailer.load_status === "loaded" || orderCheckoutTrailer.seal_id) && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Seal Status *</Label>
                  {(orderCheckoutTrailer as any).seal_status && (
                    <p className="text-xs text-muted-foreground">
                      Last recorded: <span className="capitalize font-medium">{(orderCheckoutTrailer as any).seal_status}</span>
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant={orderOverlaySealStatus === "intact" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setOrderOverlaySealStatus("intact")}
                    >
                      Intact
                    </Button>
                    <Button
                      type="button"
                      variant={orderOverlaySealStatus === "broken" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setOrderOverlaySealStatus("broken")}
                    >
                      Broken
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Fixed bottom action bar */}
          <div className="fixed bottom-0 left-0 right-0 p-4 border-t z-50 bg-background">
            <Button
              className="w-full"
              size="lg"
              onClick={() => setShowOrderConfirmDrawer(true)}
              disabled={checkoutAction.isProcessing}
            >
              {checkoutAction.isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {busyLabel}
                </>
              ) : (
                "Check Out"
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Order checkin full-page overlay */}
      {orderCheckinOverlay && (
        <div className="fixed inset-0 z-50 bg-background overflow-auto">
          <div className="p-8 pb-24">
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => {
                  setOrderCheckinOverlay(null);
                  setOrderCheckInPhoto(null);
                  setOrderCheckInSealPhoto(null);
                  setOrderCheckInLicensePlatePhoto(null);
                  setOrderCheckInBolPhoto(null);
                }}
                className="p-1.5 rounded-md hover:bg-muted transition-colors active:scale-95"
              >
                <X className="h-6 w-6" />
              </button>
              <h2 className="text-2xl font-semibold">Check in Trailer {orderCheckinOverlay.trailer_id}</h2>
            </div>

            {/* Order info */}
            <div className="rounded-lg border border-primary/30 bg-primary/10 text-foreground p-3 mb-6 flex items-center gap-3">
              <Info className="h-4 w-4 shrink-0 text-primary" />
              <p className="text-sm font-medium">
                Associated with Order{" "}
                <span className="font-semibold font-mono">{orderCheckinOverlay.unique_id}</span>
              </p>
            </div>

            {/* This check-in closes the last open leg — say so before it happens. */}
            {describeCheckInImpact(orderCheckinOverlay) && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 text-foreground p-3 mb-6 flex items-center gap-3">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                <p className="text-sm font-medium">{describeCheckInImpact(orderCheckinOverlay)}</p>
              </div>
            )}

            {/* The trailer's last event says it is standing at another site. */}
            {checkinLocationConflict && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 text-foreground p-3 mb-6 flex items-center gap-3">
                <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
                <p className="text-sm font-medium">{checkinLocationConflict}</p>
              </div>
            )}



            {/* Full check-in form */}
            <CheckInForm
              locationId={locationId}
              locationCity={locationCity}
              locationName={locationName}
              isProcessing={checkinAction.isProcessing}
              pendingPhoto={orderCheckInPhoto}
              onPhotoChange={setOrderCheckInPhoto}
              pendingPhotoRight={orderCheckInPhotoRight}
              onPhotoRightChange={setOrderCheckInPhotoRight}
              pendingSealPhoto={orderCheckInSealPhoto}
              onSealPhotoChange={setOrderCheckInSealPhoto}
              pendingLicensePlatePhoto={orderCheckInLicensePlatePhoto}
              onLicensePlatePhotoChange={setOrderCheckInLicensePlatePhoto}
              pendingBolPhoto={orderCheckInBolPhoto}
              onBolPhotoChange={setOrderCheckInBolPhoto}
              processManualAction={checkinAction.processManualAction}
              createAndProcess={checkinAction.createAndProcess}
              submitRef={orderCheckInSubmitRef}
              onReadyChange={setOrderCheckInReady}
              hideTitle
              lockedTrailerId={orderCheckinOverlay.trailer_id}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: ["yard-site-trailer-count", locationId, tenantId] });
                queryClient.invalidateQueries({ queryKey: ["trailers-on-site", locationId, tenantId] });
                queryClient.invalidateQueries({ queryKey: ["trailers-pending-checkin", locationId, tenantId] });
                setOrderCheckinOverlay(null);
                setOrderCheckInPhoto(null);
                setOrderCheckInSealPhoto(null);
                setOrderCheckInLicensePlatePhoto(null);
                setOrderCheckInBolPhoto(null);
              }}
            />
          </div>

          {/* Fixed bottom action bar */}
          <div className="fixed bottom-0 left-0 right-0 p-4 border-t z-50 bg-background">
            <Button
              className="w-full"
              size="lg"
              onClick={() => setShowOrderConfirmDrawer(true)}
              disabled={!orderCheckInReady || checkinAction.isProcessing}
            >
              {checkinAction.isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {busyLabel}
                </>
              ) : (
                "Check In"
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Order confirmation half-sheet drawer */}
      <Drawer open={showOrderConfirmDrawer} onOpenChange={setShowOrderConfirmDrawer}>
        <DrawerContent>
          <div className="p-6 pt-4">
            <h2 className="text-2xl font-bold mb-3">
              {orderCheckoutOverlay ? `Check out Trailer ${orderCheckoutOverlay.trailer_id}?` : `Check in Trailer ${orderCheckinOverlay?.trailer_id}?`}
            </h2>
            {orderCheckoutOverlay && (
              <p className="text-base text-muted-foreground">
                Checking out Trailer {orderCheckoutOverlay.trailer_id} for {orderCheckoutOverlay.unique_id} will mark the trailer as In Transit and will update the order status in the Aurora TMS.
              </p>
            )}
          </div>
          <DrawerFooter className="flex-row gap-3 pt-0">
            <Button
              variant="outline"
              className="flex-1"
              size="lg"
              onClick={() => setShowOrderConfirmDrawer(false)}
            >
              Go back
            </Button>
            <Button
              className="flex-1"
              size="lg"
              onClick={() => {
                if (orderCheckoutOverlay) {
                  handleOrderCheckoutConfirm();
                } else {
                  handleOrderCheckinConfirm();
                }
              }}
              disabled={orderCheckoutOverlay ? checkoutAction.isProcessing : checkinAction.isProcessing}
            >
              {(orderCheckoutOverlay ? checkoutAction.isProcessing : checkinAction.isProcessing) ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {busyLabel}
                </>
              ) : (
                "Confirm"
              )}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Manual checkout confirmation */}
      <AlertDialog open={showManualCheckoutConfirm} onOpenChange={setShowManualCheckoutConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Check Out Trailer</AlertDialogTitle>
            <AlertDialogDescription>
              Confirm check out of trailer{" "}
              <strong className="text-foreground">{manualTrailerId.trim()}</strong>{" "}
              from {locationName}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={checkoutAction.isProcessing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleManualCheckout(); }} disabled={checkoutAction.isProcessing}>
              {checkoutAction.isProcessing ? busyLabel : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New trailer confirmation for checkout */}
      <AlertDialog open={showNewTrailerConfirm} onOpenChange={setShowNewTrailerConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Trailer Not Found
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  <strong className="text-foreground">{manualTrailerId.trim()}</strong> does not
                  exist in the system. A new trailer record will be created.
                </p>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1.5">Trailer Type</label>
                  <Select value={newTrailerTypeId} onValueChange={setNewTrailerTypeId}>
                    <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {trailerTypes?.map((tt) => (
                        <SelectItem key={tt.id} value={tt.id}>{tt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={checkoutAction.isProcessing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleCreateAndCheckout(); }} disabled={checkoutAction.isProcessing}>
              {checkoutAction.isProcessing ? busyLabel : "Create & Check Out"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Blocking busy overlay — makes long photo uploads visibly "working"
          and prevents double-taps that used to look like a frozen screen. */}
      {anyProcessing && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm"
          role="status"
          aria-live="polite"
        >
          <div className="flex flex-col items-center gap-3 rounded-lg border bg-card px-6 py-5 shadow-lg">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm font-medium">{busyLabel}</p>
            <p className="text-xs text-muted-foreground">Keep this screen open</p>
          </div>
        </div>
      )}
    </div>
  );
}
